// Assumptions panel — every projection chart surfaces the assumptions it used
// and lets the user tweak them in-place.
//
// State is lifted so the parent (ProjectionsSection) can re-run a projection
// when any value changes. Debounced by the parent.
import { useEffect, useState } from "react";
import { Info, RotateCcw } from "lucide-react";

const DEFAULTS = {
  years: 40,
  inflation_pct: 2.5,
  default_return_pct: 7.0,
  salary_growth_pct: 3.0,
};

export default function AssumptionsPanel({ value, onChange, dense = false }) {
  const v = { ...DEFAULTS, ...(value || {}) };
  const set = (k, x) => onChange && onChange({ ...v, [k]: x });

  return (
    <div
      className="rounded-xl border border-white/10 bg-card/40 p-4 sm:p-5"
      data-testid="assumptions-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-4 h-4 text-blue-400" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Assumptions — adjust anything
        </div>
        <button
          type="button"
          onClick={() => onChange && onChange(DEFAULTS)}
          className="ml-auto text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors"
          data-testid="assumptions-reset"
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className={`grid ${dense ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-4"} gap-3`}>
        <Slider label="Projection years" min={5} max={60} step={1} value={v.years}
                onChange={(n) => set("years", n)} suffix="yr" testId="a-years" />
        <Slider label="Inflation" min={0} max={10} step={0.1} value={v.inflation_pct}
                onChange={(n) => set("inflation_pct", n)} suffix="%" testId="a-inflation" />
        <Slider label="Investment return" min={0} max={12} step={0.1} value={v.default_return_pct}
                onChange={(n) => set("default_return_pct", n)} suffix="%" testId="a-return" />
        <Slider label="Salary growth" min={0} max={12} step={0.1} value={v.salary_growth_pct}
                onChange={(n) => set("salary_growth_pct", n)} suffix="%" testId="a-salary-growth" />
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
        Educational estimates. Verify current tax and contribution figures with the appropriate
        authority. Every assumption feeds every projection on this page.
      </p>
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, suffix, testId }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  // Debounce commit so a11y (keyboard) users also trigger re-projections
  // without spamming the API on every key press.
  const commit = (n) => onChange(Number(n));
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => commit(local), 300);
    return () => clearTimeout(t);
  }, [local]);

  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        <span className="text-xs font-mono tabular-nums">{Number(local).toFixed(step < 1 ? 1 : 0)}{suffix}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step} value={local}
        onChange={(e) => setLocal(Number(e.target.value))}
        onMouseUp={() => commit(local)}
        onTouchEnd={() => commit(local)}
        onKeyUp={() => commit(local)}
        className="w-full mt-1.5 accent-primary"
        data-testid={testId}
        aria-label={label}
      />
    </label>
  );
}
